import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useTheme } from '@/context/ThemeContext';

function Particles({ count = 240, dark }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 18;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 12;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.03;
      ref.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.06) * 0.08;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.045} color={dark ? '#3EB489' : '#1A4731'} transparent opacity={dark ? 0.6 : 0.35} sizeAttenuation />
    </points>
  );
}

function Glow({ dark }) {
  return (
    <>
      <mesh position={[-4, 2, -3]}>
        <sphereGeometry args={[2.4, 24, 24]} />
        <meshBasicMaterial color="#3EB489" transparent opacity={dark ? 0.05 : 0.03} />
      </mesh>
      <mesh position={[5, -2, -4]}>
        <sphereGeometry args={[2.0, 24, 24]} />
        <meshBasicMaterial color="#C9A84C" transparent opacity={dark ? 0.045 : 0.028} />
      </mesh>
    </>
  );
}

export const CinematicBackground = () => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      data-testid="cinematic-background-canvas"
      className="fixed inset-0 -z-10 pointer-events-none"
      style={{ background: dark
        ? 'radial-gradient(1200px 700px at 15% 0%, rgba(26,71,49,0.35), transparent 55%), radial-gradient(1000px 600px at 90% 10%, rgba(201,168,76,0.10), transparent 55%), #07090A'
        : 'radial-gradient(1200px 700px at 15% 0%, rgba(168,230,206,0.45), transparent 55%), radial-gradient(1000px 600px at 90% 10%, rgba(201,168,76,0.12), transparent 55%), #F4FAF7' }}
    >
      {!reduced && (
        <Canvas camera={{ position: [0, 0, 8], fov: 60 }} dpr={isMobile ? 1 : [1, 1.6]} gl={{ antialias: !isMobile }}>
          <Particles count={isMobile ? 90 : 240} dark={dark} />
          <Glow dark={dark} />
        </Canvas>
      )}
    </div>
  );
};
